import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Input,
  Avatar,
  AvatarFallback,
  Badge,
} from '@t-one-internal/design-system'
import {
  Check,
  X,
  Funnel,
  CaretRight,
  CaretLeft,
  MagnifyingGlass,
  CheckSquare,
  Square,
} from '@phosphor-icons/react'
import type { Employee } from '../../../../types/recognition'
import type { FilterField } from './useGiveRecognitionForm'

interface RecipientStepProps {
  search: string
  onSearchChange: (value: string) => void
  employees: Employee[]
  selectedRecipients: Employee[]
  onToggle: (employee: Employee) => void
  onRemove: (employeeId: string) => void
  onSelectAllVisible: () => void
  activeFilters: Record<string, string[]>
  onFiltersChange: (filters: Record<string, string[]>) => void
  filterFields: FilterField[]
  totalActiveFilters: number
  minRecipients?: number
}

// ─── Filter Panel ─────────────────────────────────────────────────────────────

interface FilterPanelProps {
  fields: FilterField[]
  activeFilters: Record<string, string[]>
  onApply: (filters: Record<string, string[]>) => void
  onClose: () => void
}

const FilterPanel: React.FC<FilterPanelProps> = ({ fields, activeFilters, onApply, onClose }) => {
  const [draft, setDraft] = useState<Record<string, string[]>>({ ...activeFilters })
  const [activeField, setActiveField] = useState<FilterField | null>(null)
  const [fieldSearch, setFieldSearch] = useState('')

  const toggleValue = (key: string, value: string) => {
    setDraft((prev) => {
      const current = prev[key] ?? []
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      return { ...prev, [key]: next }
    })
  }

  const clearField = (key: string) => {
    setDraft((prev) => ({ ...prev, [key]: [] }))
  }

  const totalSelected = Object.values(draft).reduce((sum, v) => sum + v.length, 0)

  const visibleOptions = activeField
    ? activeField.options.filter((o) =>
        fieldSearch === '' || o.toLowerCase().includes(fieldSearch.toLowerCase()),
      )
    : []

  if (fields.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className="text-xs text-muted-foreground">No filters available yet — filters populate once employees are assigned to org units.</p>
        <button
          onClick={onClose}
          className="mt-3 w-full py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-semibold"
        >
          Close
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col max-h-72">
      {activeField ? (
        // ── Field options sub-panel ──────────────────────────────────────────
        <>
          {/* Sub-panel header */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b">
            <button
              onClick={() => { setActiveField(null); setFieldSearch('') }}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <span className="text-xs font-semibold text-foreground flex-1">{activeField.label}</span>
            {(draft[activeField.key] ?? []).length > 0 && (
              <button
                onClick={() => clearField(activeField.key)}
                className="text-[10px] text-muted-foreground hover:text-destructive transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          {/* Option search */}
          <div className="px-3 py-2 border-b">
            <div className="flex items-center gap-1.5 bg-muted rounded-md px-2 py-1">
              <MagnifyingGlass size={12} className="text-muted-foreground shrink-0" />
              <input
                autoFocus
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
                placeholder={`Search ${activeField.label.toLowerCase()}...`}
                className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="flex-1 overflow-y-auto">
            <div className="py-1">
              {visibleOptions.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">No options found</p>
              ) : (
                visibleOptions.map((opt) => {
                  const isChecked = (draft[activeField.key] ?? []).includes(opt)
                  return (
                    <button
                      key={opt}
                      onClick={() => toggleValue(activeField.key, opt)}
                      className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors"
                    >
                      {isChecked ? (
                        <CheckSquare size={14} weight="fill" className="text-primary shrink-0" />
                      ) : (
                        <Square size={14} className="text-muted-foreground shrink-0" />
                      )}
                      <span className="truncate">{opt}</span>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        </>
      ) : (
        // ── Category list main panel ─────────────────────────────────────────
        <>
          {/* Panel search */}
          <div className="px-3 py-2 border-b">
            <div className="flex items-center gap-1.5 bg-muted rounded-md px-2 py-1">
              <MagnifyingGlass size={12} className="text-muted-foreground shrink-0" />
              <input
                autoFocus
                placeholder="Search fields..."
                className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
          </div>

          {/* Filter categories */}
          <div className="flex-1 overflow-y-auto">
            <div className="py-1">
              {fields.map((field) => {
                const count = (draft[field.key] ?? []).length
                return (
                  <button
                    key={field.key}
                    onClick={() => { setActiveField(field); setFieldSearch('') }}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-xs text-foreground hover:bg-muted transition-colors"
                  >
                    <span className="font-medium">{field.label}</span>
                    <div className="flex items-center gap-1.5">
                      {count > 0 && (
                        <span className="bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                          {count}
                        </span>
                      )}
                      <CaretRight size={12} className="text-muted-foreground" />
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Apply button */}
      <div className="border-t p-2">
        <button
          onClick={() => { onApply(draft); onClose() }}
          className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:bg-primary/90 transition-colors"
        >
          Apply{totalSelected > 0 ? ` (${totalSelected})` : ''}
        </button>
      </div>
    </div>
  )
}

// ─── RecipientStep ────────────────────────────────────────────────────────────

const RecipientStep: React.FC<RecipientStepProps> = ({
  search,
  onSearchChange,
  employees,
  selectedRecipients,
  onToggle,
  onRemove,
  onSelectAllVisible,
  activeFilters,
  onFiltersChange,
  filterFields,
  totalActiveFilters,
  minRecipients = 1,
}) => {
  const [filterOpen, setFilterOpen] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)

  const selectedIds = new Set(selectedRecipients.map((e) => e.id))
  const allVisibleSelected =
    employees.length > 0 && employees.every((e) => selectedIds.has(e.id))

  // Close filter panel on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleApplyFilters = useCallback(
    (filters: Record<string, string[]>) => onFiltersChange(filters),
    [onFiltersChange],
  )

  // Active filter chips below the filter row
  const activeChips = Object.entries(activeFilters).flatMap(([key, values]) =>
    values.map((v) => ({ key, value: v })),
  )

  return (
    <div>
      <p className="text-sm font-semibold text-foreground mb-3">
        Who do you want to recognize?
      </p>

      {/* Selected recipients chips */}
      {selectedRecipients.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {selectedRecipients.map((emp) => (
            <Badge
              key={emp.id}
              variant="secondary"
              className="flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs font-medium"
            >
              {emp.name}
              <button
                onClick={() => onRemove(emp.id)}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5 transition-colors"
              >
                <X size={10} weight="bold" />
              </button>
            </Badge>
          ))}
          <span className="text-xs text-muted-foreground self-center ml-1">
            {selectedRecipients.length} selected
          </span>
        </div>
      )}

      {/* Search + Filter — filterRef wraps both button and panel so clicks inside panel don't close it */}
      <div ref={filterRef} className="mb-2">
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
            placeholder="Search employees..."
            className="flex-1 h-8 text-sm"
          />
          <button
            onClick={() => setFilterOpen((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 h-8 rounded-md text-xs font-medium border transition-colors shrink-0 ${
              totalActiveFilters > 0
                ? 'bg-primary/10 border-primary/30 text-primary'
                : 'bg-background border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            <Funnel size={13} weight={totalActiveFilters > 0 ? 'fill' : 'regular'} />
            Filter
            {totalActiveFilters > 0 && (
              <span className="bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                {totalActiveFilters}
              </span>
            )}
          </button>
        </div>

        {/* Inline filter panel — inside filterRef so outside-click doesn't close on panel interaction */}
        {filterOpen && (
          <div className="mt-1 border border-border rounded-lg bg-popover shadow-sm overflow-hidden flex flex-col max-h-[240px]">
            <FilterPanel
              fields={filterFields}
              activeFilters={activeFilters}
              onApply={handleApplyFilters}
              onClose={() => setFilterOpen(false)}
            />
          </div>
        )}
      </div>

      {/* Active filter chips */}
      {!filterOpen && activeChips.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {activeChips.map(({ key, value }) => (
            <span
              key={`${key}-${value}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary border border-primary/20 rounded-full text-[11px] font-medium"
            >
              {value}
              <button
                onClick={() => {
                  const updated = { ...activeFilters, [key]: (activeFilters[key] ?? []).filter((v) => v !== value) }
                  onFiltersChange(updated)
                }}
                className="hover:opacity-70 transition-opacity"
              >
                <X size={9} weight="bold" />
              </button>
            </span>
          ))}
          <button
            onClick={() => onFiltersChange({})}
            className="text-[11px] text-muted-foreground hover:text-foreground transition-colors self-center"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Select All + employee count */}
      <div className="flex items-center justify-between mb-1.5">
        <button
          onClick={onSelectAllVisible}
          className={`text-xs font-medium transition-colors ${
            allVisibleSelected ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {allVisibleSelected ? 'Deselect All' : 'Select All'} ({employees.length})
        </button>
        {minRecipients > 1 && selectedRecipients.length < minRecipients && (
          <span className="text-[11px] text-muted-foreground">
            Select at least {minRecipients} people
          </span>
        )}
      </div>

      {/* Employee list */}
      <div className="h-[180px] overflow-y-auto">
        <div className="space-y-0.5 pr-1">
          {employees.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              No employees match your filters
            </p>
          ) : (
            employees.map((emp) => {
              const isSelected = selectedIds.has(emp.id)
              return (
                <button
                  key={emp.id}
                  onClick={() => onToggle(emp)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                    isSelected
                      ? 'bg-primary/10 border border-primary/30'
                      : 'hover:bg-muted border border-transparent'
                  }`}
                >
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="text-[10px] font-bold bg-primary/20 text-primary">
                      {emp.initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground leading-tight">
                      {emp.name}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {[emp.title, emp.team].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {isSelected && (
                    <Check size={16} weight="bold" className="text-primary shrink-0" />
                  )}
                </button>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

export default RecipientStep
